import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

interface NDAAcceptance {
  id: string;
  user_email: string;
  company_name: string;
  nda_version: string;
  accepted_at: string;
  ip_address: string | null;
}

export function NDAAcceptanceTable() {
  const [acceptances, setAcceptances] = useState<NDAAcceptance[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    fetchAcceptances();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    const { data } = await supabase.from('companies').select('id, name').order('name');
    setCompanies(data || []);
  };

  const fetchAcceptances = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('nda-list-acceptances', {});

      if (error) throw error;
      setAcceptances(data.acceptances || []);
    } catch (error) {
      console.error('Error fetching acceptances:', error);
    }
    setLoading(false);
  };

  const filteredAcceptances = acceptances.filter((acc) => {
    const matchesSearch =
      acc.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.company_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCompany = companyFilter === 'all' || acc.company_name === companyFilter;
    return matchesSearch && matchesCompany;
  });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by company" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {companies.map((company) => (
              <SelectItem key={company.id} value={company.name}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Accepted</TableHead>
              <TableHead>IP Address</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAcceptances.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No acceptances found
                </TableCell>
              </TableRow>
            ) : (
              filteredAcceptances.map((acceptance) => (
                <TableRow key={acceptance.id}>
                  <TableCell className="font-medium">{acceptance.user_email}</TableCell>
                  <TableCell>{acceptance.company_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{acceptance.nda_version}</Badge>
                  </TableCell>
                  <TableCell>{format(new Date(acceptance.accepted_at), 'PPp')}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {acceptance.ip_address || '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-success">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">Accepted</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
